import { HStack, Tabs, Text } from '@chakra-ui/react';
import CockpitDialog from '@components/cockpit/CockpitDialog';
import { CockpitEditorLayout } from '@components/cockpit/layouts/CockpitEditorLayout';
import ModifiedTag from '@components/common/form/ModifiedField';
import { createToast, ToastStatusEnum } from '@components/ui/toaster';
import { useAppSelector } from '@hooks/redux.hooks';
import useValidation from '@hooks/useValidation';
import { selectActiveSite } from '@redux/authent/authent.selectors';
import { fleetSchema } from '@redux/utm/utm.schema';
import { PilotSelectors, StrategicDroneSelectors } from '@redux/utm/utm.selectors';
import { useCreateFleetMutation, useUpdateFleetMutation } from '@services/utm/fleet.api';
import { areSelectedFieldsEqual, createErrorMessage } from '@utils/common.utils';
import { normalizeOne } from '@utils/schema.utils';
import { fleetSchemaValidation } from '@validations/utm.schema';
import { Form, Formik, FormikHelpers } from 'formik';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { v4 as uuidv4 } from 'uuid';

import { DroneClearanceEnum } from '@/types/utm/drone.types';
import { Fleet, NormalizedFleet } from '@/types/utm/fleet.type';

import { Breadcrumb } from '../../Breadcrumb';
import { useUtmContext, UtmItemTypeEnum } from '../UtmManagement';
import ChipsTab from './ChipsTab';
import DronesAndPilotsTab from './DronesAndPilotsTab';
import FleetGeneralTab from './FleetGeneralTab';

const emptyFleet: Partial<NormalizedFleet> = {
  name: '',
  drones: [],
  pilots: [],
  strategicClearance: DroneClearanceEnum.FULL_CLEARANCE_WITHIN_FLIGHTPLAN,
  chips: [],
};

export default function FleetFormContainer() {
  const { formatMessage } = useIntl();

  const { editedItem, setEditedItem } = useUtmContext();
  const fleet = (editedItem?.value ?? emptyFleet) as Partial<NormalizedFleet>;

  const activeSite = useAppSelector(selectActiveSite);
  const [open, setOpen] = useState(false);
  const [createFleet, { isLoading: isLoadingCreate }] = useCreateFleetMutation();
  const [updateFleet, { isLoading: isLoadingUpdate }] = useUpdateFleetMutation();
  const isNewFleet = fleet.id === undefined;
  const validationSchema = useValidation(fleetSchemaValidation);
  const pilots = useAppSelector(PilotSelectors.selectAllPilots);
  const drones = useAppSelector(StrategicDroneSelectors.selectAllStrategicDrones);
  const handleSubmit = (values: Partial<NormalizedFleet>, formikHelpers: FormikHelpers<Partial<NormalizedFleet>>) => {
    const { resetForm } = formikHelpers;
    if (isNewFleet) {
      createFleet({
        ...values,
        site: activeSite?.code ?? 'local',
        drones: drones.filter((drone) => values.drones?.includes(drone.id.toString())),
        pilots: pilots.filter((pilot) => values.pilots?.includes(pilot.code)),
        code: uuidv4(),
      } as Fleet)
        .unwrap()
        .then((fleet) => {
          setOpen(false);
          createToast(
            formatMessage({ id: 'components.utm.fleets.message.add' }, { name: fleet.name }),
            ToastStatusEnum.SUCCESS,
          );
          setEditedItem({ type: UtmItemTypeEnum.FLEET, value: normalizeOne(fleet, fleetSchema) as NormalizedFleet });
          resetForm({ values });
        })
        .catch((error) => createToast(createErrorMessage(error), ToastStatusEnum.ERROR));
    } else {
      updateFleet({
        ...values,
        drones: drones.filter((drone) => values.drones?.includes(drone.id.toString())),
        pilots: pilots.filter((pilot) => values.pilots?.includes(pilot.code)),
      } as Fleet)
        .unwrap()
        .then(() => {
          setOpen(false);
          createToast(
            formatMessage({ id: 'components.utm.fleets.message.update' }, { name: values.name }),
            ToastStatusEnum.SUCCESS,
          );
          resetForm({ values });
        })
        .catch((error) => createToast(createErrorMessage(error), ToastStatusEnum.ERROR));
    }
  };

  return (
    <Formik
      initialValues={fleet}
      validationSchema={validationSchema}
      validateOnBlur
      enableReinitialize
      onSubmit={handleSubmit}
    >
      {({ initialValues, isValid, submitForm, values }) => {
        const hasGeneralTabChanged = !areSelectedFieldsEqual(values, initialValues, ['name', 'strategicClearance']);
        const hasDronesAndPilotsTabChanged = !areSelectedFieldsEqual(values, initialValues, ['drones', 'pilots']);
        const hasChipsTabChanged = !areSelectedFieldsEqual(values, initialValues, ['chips']);

        const hasChanged = () => {
          return hasGeneralTabChanged || hasDronesAndPilotsTabChanged || hasChipsTabChanged;
        };
        return (
          <Form style={{ width: '100%', height: '100%' }}>
            <CockpitEditorLayout
              dialog={
                <CockpitDialog
                  open={open}
                  isLoading={isLoadingCreate || isLoadingUpdate}
                  confirmLabel={formatMessage({ id: 'components.utm.save' })}
                  cancelLabel={formatMessage({ id: 'components.utm.cancelSave' })}
                  onConfirm={submitForm}
                  onCancel={() => setOpen(false)}
                >
                  <Text color="neutral.100" width="100%" whiteSpace="pre-line">
                    <FormattedMessage
                      id={`components.utm.fleets.popup.${isNewFleet ? 'descriptionSaveNew' : 'descriptionSave'}`}
                      values={{ name: values.name }}
                    />
                  </Text>
                </CockpitDialog>
              }
              breadcrumbs={
                <Breadcrumb
                  items={[
                    {
                      label: formatMessage({ id: 'components.utm.fleets.title' }),
                      action: () => setEditedItem(null),
                    },
                    {
                      label: isNewFleet ? formatMessage({ id: 'components.utm.fleets.newFleet' }) : fleet.name,
                    },
                  ]}
                />
              }
              onBack={() => setEditedItem(null)}
              onSave={() => {
                if (!isValid || !hasChanged()) {
                  return;
                }
                if (editedItem!.value === null) {
                  submitForm();
                } else {
                  setOpen(true);
                }
              }}
              isSaving={isLoadingCreate || isLoadingUpdate}
              isSaveDisabled={!isValid || !hasChanged()}
            >
              <Tabs.Root
                orientation="vertical"
                defaultValue="general"
                size="md"
                lazyMount
                variant="vertical"
                userSelect="none"
              >
                <Tabs.List>
                  <Tabs.Trigger value="general">
                    <HStack width="100%">
                      <FormattedMessage id="components.utm.fleets.tab.general.title" />
                      {hasGeneralTabChanged && <ModifiedTag />}
                    </HStack>
                  </Tabs.Trigger>
                  <Tabs.Trigger value="dronesAndPilots">
                    <HStack width="100%">
                      <FormattedMessage id="components.utm.fleets.tab.dronesAndPilots.title" />
                      {hasDronesAndPilotsTabChanged && <ModifiedTag />}
                    </HStack>
                  </Tabs.Trigger>
                  <Tabs.Trigger value="chips">
                    <HStack width="100%">
                      <FormattedMessage id="components.utm.fleets.tab.chips.title" />
                      {hasChipsTabChanged && <ModifiedTag />}
                    </HStack>
                  </Tabs.Trigger>
                </Tabs.List>
                <Tabs.ContentGroup backgroundColor="neutral.700">
                  <Tabs.Content value="general">
                    <FleetGeneralTab />
                  </Tabs.Content>
                  <Tabs.Content value="dronesAndPilots">
                    <DronesAndPilotsTab />
                  </Tabs.Content>
                  <Tabs.Content value="chips" overflow="hidden" overflowY="auto" className="cockpit-scrollbar-900">
                    <ChipsTab />
                  </Tabs.Content>
                </Tabs.ContentGroup>
              </Tabs.Root>
            </CockpitEditorLayout>
          </Form>
        );
      }}
    </Formik>
  );
}
